// File: Home.js or Home.jsx
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import WeeklySelector from "../Components/Calendar/WeeklySelector";

import api from "../Utils/api";
import CalendarGrid from "../Components/Calendar/CalendarGrid";
import DashboardCards from "../Components/Home/DashboardCards";
import UserProfileDialog from "../Components/User/UserProfileDialog";
import Spinner from "../Components/Spinner";

const Home = () => {
  const user = useSelector((state) => state.user.user);

  const [openUserProfile, setOpenUserProfile] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [currentStartDate, setCurrentStartDate] = useState(new Date());

  const [openShifts, setOpenShifts] = useState([]);
  const [unavailables, setUnavailables] = useState([]);
  const [shiftSwaps, setShiftSwaps] = useState([]);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [shiftOffers, setShiftOffers] = useState([]);

  const [shifts, setShifts] = useState([]);

  const [lateShifts, setLateShifts] = useState([]);
  const [todayLeaves, setTodayLeaves] = useState([]);

  useEffect(() => {
    getTodayInfo();
  }, []);

  const pad = (n) => n.toString().padStart(2, "0");

  const getLocalYMD = (date) => {
    const yyyy = date.getFullYear();
    const mm = pad(date.getMonth() + 1); // Months are 0-based
    const dd = pad(date.getDate());
    return `${yyyy}-${mm}-${dd}`;
  };

  // Fetch counts for schedule types
  const getDashboardData = async (currentStartDate) => {
    setIsLoading(true);
    try {
      setCurrentStartDate(currentStartDate);

      const endDate = new Date(currentStartDate);
      endDate.setDate(endDate.getDate() + 6);

      // console.log(currentStartDate);
      // console.log(endDate);

      // console.log(currentStartDate.toLocaleString());
      // console.log(endDate.toISOString());

      const res = await api.post("/report/schedule/week_data", {
        startTime: getLocalYMD(currentStartDate),
        endTime: getLocalYMD(endDate), // send endDate as well if your API supports it
      });
      setIsLoading(false);
      setOpenShifts(res.data.schedules.open);
      setShiftOffers(res.data.schedules.shift_offer);
      setShiftSwaps(res.data.schedules.shift_swap);

      const formattedLeaveRequests = res.data.leave.map((leave) => {
        const leaveType = leave.type; // Save original type
        return {
          ...leave,
          type: "leave", // Override type to "leave"
          leaveType: leaveType, // Store original type in leaveType
        };
      });

      const formattedUnavailables = res.data.unavailable.map((item) => ({
        ...item,
        type: "unavailable",
      }));

      setLeaveRequests(formattedLeaveRequests);
      setUnavailables(formattedUnavailables);

      const leavesWithType = res.data.leave.map((item) => ({
        ...item,
        type: "leave",
      }));

      const unavailablesWithType = res.data.unavailable.map((item) => ({
        ...item,
        type: "unavailable",
      }));

      setShifts([
        ...res.data.schedules.shift,
        ...res.data.schedules.open,
        ...res.data.schedules.shift_offer,
        ...res.data.schedules.shift_swap,
        ...leavesWithType,
        ...unavailablesWithType,
      ]);
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
    }
  };

  function calculateTotalWorkedHours(schedules) {
    let totalMilliseconds = 0;

    schedules.forEach((schedule) => {
      if (schedule.type == "leave" || schedule.type == "unavailable") return;

      if (!schedule.startTime || !schedule.endTime) return; // skip if missing

      const start = new Date(schedule.startTime);
      const end = new Date(schedule.endTime);

      let durationMs = end - start;
      if (durationMs <= 0) return; // invalid duration

      if (schedule.breakStartTime && schedule.breakEndTime) {
        const breakStart = new Date(schedule.breakStartTime);
        const breakEnd = new Date(schedule.breakEndTime);

        const breakDurationMs = breakEnd - breakStart;

        if (breakDurationMs > 0 && breakDurationMs < durationMs) {
          durationMs -= breakDurationMs;
        }
      }

      totalMilliseconds += durationMs;
    });

    // Convert milliseconds to hours (decimal)
    const totalHours = totalMilliseconds / (1000 * 60 * 60);
    return totalHours;
  }

  const getTodayInfo = async () => {
    const res = await api.get("/schedule/today_happening");
    if (res.data.success) {
      setLateShifts(res.data.lateShifts);

      const formattedLeaveRequests = res.data.leaveRequests.map((leave) => {
        const leaveType = leave.type; // Save original type
        return {
          ...leave,
          type: "leave", // Override type to "leave"
          leaveType: leaveType, // Store original type in leaveType
        };
      });

      setTodayLeaves(formattedLeaveRequests);
    }
  };

  return (
    <div className="flex max-h-[calc(100vh-50px)] overflow-y-auto p-6 ">
      {/* Main Content */}

      {isLoading && <Spinner />}
      <main className="flex-1 ml-6">
        <DashboardCards
          setUserId={setSelectedUserId}
          openDialog={setOpenUserProfile}
          openShifts={openShifts}
          shiftSwaps={shiftSwaps}
          shiftOffers={shiftOffers}
          leaveRequests={leaveRequests}
          unavailables={unavailables}
          lateShifts={lateShifts}
          todayLeaves={todayLeaves}
        />

        {/* Calendar Section */}
        <div className="mt-8">
          <h2 className="text-3xl font-semibold">Calendar</h2>
          <hr className="mt-2 mb-2 w-full" />
          <WeeklySelector
            totalHours={calculateTotalWorkedHours(shifts)}
            getMyScheduels={getDashboardData}
          />

          {/* Week View */}
          <div className="flex flex-wrap  border-gray-200 mt-8 mb-40">
            <CalendarGrid
              startDate={currentStartDate}
              period={7}
              schedules={shifts}
              openDialog={() => {}}
              openModifyDialog={() => {}}
              displayDay={true}
            />
            <CalendarGrid
              simple={true}
              startDate={currentStartDate}
              location={true}
              period={7}
              // getSchedules={() => getSchedules(currentStartDate)}
              schedules={shifts}
              openDialog={() => {}}
              openModifyDialog={() => {}}
            />
          </div>
        </div>
      </main>

      {openUserProfile && (
        <UserProfileDialog
          userID={selectedUserId}
          onClose={() => setOpenUserProfile(false)}
        />
      )}
    </div>
  );
};

export default Home;
